import { useState, type FormEvent } from 'react';
import {
  PRIORITY_LABEL_PT,
  partsInZone,
  zonedPartsToUtc,
  type Priority,
  type Task,
} from '@cleartask/shared';
import { useTimeZone } from '../../session.js';
import { useToast } from '../../components/ui/Toast.js';
import { useLists } from '../lists/useLists.js';
import { useTags } from '../tags/useTags.js';
import { ParteDe, Subtarefas } from './Subtarefas.js';
import { useDeleteTask, useUpdateTask } from './useTasks.js';

interface TaskDetailProps {
  task: Task;
  onClose: () => void;
  /**
   * Abre outra tarefa no lugar desta. Opcional porque nem toda tela que monta
   * o painel navega por endereco — quem nao passa simplesmente nao mostra o
   * atalho para a tarefa-pai.
   */
  onAbrirTarefa?: (id: string) => void;
  /**
   * Painel encaixado ao lado da lista (aba Tarefas em tela larga), e nao
   * gaveta sobreposta. Muda o papel do elemento e o que o Salvar faz depois.
   */
  fixo?: boolean;
}

/**
 * Classes da gaveta (celular e `md`) e do painel fixo (aba Tarefas no `lg`).
 *
 * A gaveta e `z-[25]`, acima da barra de abas (`z-20`): com o mesmo z, a barra
 * vinha depois no DOM, ficava POR CIMA e cobria a faixa de baixo da gaveta —
 * justamente onde mora o Salvar. E `dvh`, e nao `vh`: no celular o `vh` conta a
 * altura com a barra de endereco recolhida, e o fim da gaveta ficava abaixo da
 * tela. Em paisagem a largura passa do `md`, a gaveta vira painel lateral e o
 * defeito sumia — que e como ele foi relatado.
 *
 * O painel fixo nao e `fixed`: ocupa a propria coluna da tela, `sticky` no topo
 * e com a altura da tela, para rolar por dentro enquanto a lista rola por fora.
 */
export const CLASSE_GAVETA =
  'fixed inset-x-0 bottom-0 z-[25] max-h-[85dvh] overflow-y-auto rounded-t border-t border-border bg-surface px-4 pt-3 shadow-card md:inset-y-0 md:left-auto md:right-0 md:max-h-none md:w-detail md:rounded-none md:border-l md:border-t-0';
export const CLASSE_PAINEL_FIXO =
  'sticky top-0 h-dvh w-detail shrink-0 self-start overflow-y-auto border-l border-border bg-surface px-4 pt-3';

const PRIORIDADES: Priority[] = [0, 1, 2, 3];

/**
 * Antecedencias oferecidas para o lembrete, em minutos antes do prazo.
 *
 * Relativas, e nao absolutas, porque e assim que a decisao e tomada: ninguem
 * pensa "avise as 13:50", pensa "avise dez minutos antes". Guardar a
 * ANTECEDENCIA no formulario tambem faz o lembrete acompanhar o prazo se ele
 * for alterado antes de salvar — com um horario absoluto, mudar o prazo
 * deixaria para tras um aviso apontando para o horario antigo, e nada na tela
 * denunciaria.
 *
 * O que vai para o banco continua sendo epoch absoluto (`reminder_at`): o cron
 * de 5 min pergunta "quais ja venceram?", e uma antecedencia relativa o
 * obrigaria a recalcular a resposta a cada ciclo.
 */
const LEMBRETE_PRESETS: { minutos: number; label: string }[] = [
  { minutos: 0, label: 'Na hora do prazo' },
  { minutos: 10, label: '10 minutos antes' },
  { minutos: 60, label: '1 hora antes' },
  { minutos: 60 * 24, label: '1 dia antes' },
];

/** `''` = sem lembrete · `'custom'` = horario proprio · resto = minutos antes. */
type ModoLembrete = string;

const SEM_LEMBRETE: ModoLembrete = '';
const LEMBRETE_PROPRIO: ModoLembrete = 'custom';

/**
 * Estado inicial do campo a partir do que esta gravado.
 *
 * Um lembrete que casa exatamente com uma das antecedencias volta como preset;
 * qualquer outro volta como horario proprio, com o valor visivel. Mostrar
 * "1 hora antes" para um lembrete de 47 minutos seria a interface mentindo
 * sobre o dado que ela mesma vai regravar no proximo Salvar.
 */
function lembreteInicial(
  task: Task,
  timeZone: string,
): { modo: ModoLembrete; proprio: string } {
  if (task.reminderAt === null) return { modo: SEM_LEMBRETE, proprio: '' };

  if (task.dueAt !== null) {
    const minutosAntes = (task.dueAt - task.reminderAt) / 60_000;
    if (LEMBRETE_PRESETS.some((p) => p.minutos === minutosAntes)) {
      return { modo: String(minutosAntes), proprio: '' };
    }
  }

  return { modo: LEMBRETE_PROPRIO, proprio: toLocalInput(task.reminderAt, timeZone) };
}

/** epoch -> 'YYYY-MM-DDTHH:MM' na hora de parede do usuario. */
function toLocalInput(epochMs: number, timeZone: string): string {
  const p = partsInZone(timeZone, epochMs);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** 'YYYY-MM-DDTHH:MM' -> epoch, interpretado no fuso do USUARIO. */
function fromLocalInput(value: string, timeZone: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;

  const [, year, month, day, hour, minute] = match;
  return zonedPartsToUtc(timeZone, {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
  });
}

/**
 * A permissao de notificacao deste navegador, lida de forma sincrona.
 *
 * Deliberadamente sem `usePush`: aquele hook faz `GET /push/key` e espera o
 * service worker, e pagar uma requisicao a cada abertura do painel de detalhe
 * para decidir se mostra uma DICA custaria mais do que ela vale. Aqui basta a
 * pergunta que importa — "esta pessoa ja autorizou?" — e ela nao custa nada.
 * Quem manda no assunto continua sendo a tela de Ajustes.
 */
function notificacoesAutorizadas(): boolean {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

/**
 * Painel de detalhe — bottom sheet no mobile, drawer lateral no desktop
 * (spec 9.6).
 *
 * Salvamento explicito, e nao a cada tecla: a spec 10.7 pede que o botao
 * "Salvar" produza o toast "Salvo", e um autosave silencioso deixaria o
 * usuario sem sinal de que a alteracao pegou. Excluir continua desfazivel.
 */
export function TaskDetail({ task, onClose, onAbrirTarefa, fixo = false }: TaskDetailProps) {
  const timeZone = useTimeZone();
  const { showToast } = useToast();
  const { data: listsData } = useLists();
  const { data: tagsData } = useTags();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();

  const [title, setTitle] = useState(task.title);
  const [notes, setNotes] = useState(task.notes ?? '');
  const [listId, setListId] = useState(task.listId ?? '');
  const [priority, setPriority] = useState<Priority>(task.priority);
  // Texto, e nao numero: um `useState<number>` obrigaria a escolher o que o
  // campo vazio significa no meio da digitacao, e apagar para trocar de valor
  // passaria por zero — que aqui quer dizer "sem estimativa".
  const [estimatePomos, setEstimatePomos] = useState(
    task.estimatePomos === 0 ? '' : String(task.estimatePomos),
  );
  const [tagIds, setTagIds] = useState<string[]>(task.tagIds);
  const [due, setDue] = useState(
    task.dueAt === null ? '' : toLocalInput(task.dueAt, timeZone),
  );

  const inicial = lembreteInicial(task, timeZone);
  const [modoLembrete, setModoLembrete] = useState(inicial.modo);
  const [lembreteProprio, setLembreteProprio] = useState(inicial.proprio);

  const dueAt = due === '' ? null : fromLocalInput(due, timeZone);
  const temPrazo = dueAt !== null;

  /** O epoch que sera gravado em `reminder_at`, derivado durante o render. */
  const reminderAt = (() => {
    if (modoLembrete === SEM_LEMBRETE) return null;
    if (modoLembrete === LEMBRETE_PROPRIO) {
      return lembreteProprio === '' ? null : fromLocalInput(lembreteProprio, timeZone);
    }
    // Preset sem prazo nao existe: "1 hora antes" de nada nao e um horario.
    // O seletor so os oferece com prazo, e isto e a rede de seguranca.
    return dueAt === null ? null : dueAt - Number(modoLembrete) * 60_000;
  })();

  const lembreteNoPassado = reminderAt !== null && reminderAt <= Date.now();

  function save(event: FormEvent) {
    event.preventDefault();

    updateTask.mutate({
      id: task.id,
      patch: {
        title: title.trim(),
        notes: notes.trim() === '' ? null : notes,
        listId: listId === '' ? null : listId,
        priority,
        /*
          Zero e "sem estimativa", e nao "estimei zero" — e a leitura ja trata
          assim: `estimate_pomos` nasce 0 no DDL e o card so mostra a coluna
          previsto x realizado quando ha estimativa. Campo vazio grava 0 pelo
          mesmo motivo: e como se apaga uma estimativa.

          O gasto REAL continua vindo das sessoes (`countCompletedPomosForTask`)
          e nao e somado aqui de proposito: somar destruiria a comparacao entre
          o que se previu e o que se gastou, que e a unica coisa que uma
          estimativa serve para dizer.
        */
        estimatePomos: estimatePomos.trim() === '' ? 0 : Number(estimatePomos),
        tagIds,
        dueAt,
        // Uma data digitada com hora deixa de ser "dia inteiro".
        allDay: dueAt === null ? task.allDay : false,
        /*
          `reminderAt` so entra no patch quando MUDOU, e a razao nao e economia
          de bytes: o servidor limpa `reminder_sent_at` sempre que este campo
          chega (apps/api/src/db/tasks.ts), porque mexer no horario do lembrete
          reabre o envio. Mandar o mesmo valor ao corrigir um titulo faria o
          cron reenviar um aviso ja entregue — notificacao repetida nascida de
          uma edicao que nada tinha a ver com o lembrete.
        */
        ...(reminderAt === task.reminderAt ? {} : { reminderAt }),
      },
    });

    showToast({ message: 'Salvo' });
    // No painel fixo a tarefa continua selecionada, como no TickTick: fechar
    // trocaria o que a pessoa acabou de editar pelo estado vazio, e o proximo
    // ajuste na mesma tarefa pediria um clique a mais na lista. Na gaveta,
    // fechar devolve a lista que ela cobria.
    if (!fixo) onClose();
  }

  return (
    <aside
      // Encaixado ao lado da lista o painel nao e dialogo: nao cobre nada nem
      // prende o foco, e anunciar "dialogo" faria o leitor de tela esperar um
      // modal. Sem `role`, o <aside> e uma regiao complementar com nome.
      role={fixo ? undefined : 'dialog'}
      aria-label={`Detalhe de ${task.title}`}
      className={fixo ? CLASSE_PAINEL_FIXO : CLASSE_GAVETA}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
    >
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-display text-md font-semibold">Detalhe</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar detalhe"
          className="min-h-touch rounded-sm px-2 text-text-mut hover:bg-surface-2"
        >
          ✕
        </button>
      </div>

      {/*
        No detalhe de uma FILHA, o vinculo com o pai vem primeiro: concluir uma
        etapa sem saber de qual trabalho ela faz parte e decidir no escuro. Ela
        aparece solta nas listas (ver `Subtarefas.tsx`), entao este e o unico
        lugar que conta a historia.
      */}
      {task.parentId !== null && onAbrirTarefa ? (
        <div className="mb-3">
          <ParteDe paiId={task.parentId} onAbrirPai={() => onAbrirTarefa(task.parentId as string)} />
        </div>
      ) : null}

      <form onSubmit={save} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="detail-title" className="text-sm text-text-mut">
            Título
          </label>
          <input
            id="detail-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            className="min-h-touch rounded border border-border bg-surface px-3 text-base"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="detail-list" className="text-sm text-text-mut">
            Lista
          </label>
          <select
            id="detail-list"
            value={listId}
            onChange={(event) => setListId(event.target.value)}
            className="min-h-touch rounded border border-border bg-surface px-3 text-base"
          >
            <option value="">Sem lista</option>
            {(listsData?.lists ?? []).map((list) => (
              <option key={list.id} value={list.id}>
                {list.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="detail-due" className="text-sm text-text-mut">
            Prazo
          </label>
          <input
            id="detail-due"
            type="datetime-local"
            value={due}
            onChange={(event) => {
              const valor = event.target.value;

              // Apagar o prazo com uma ANTECEDENCIA selecionada tira a
              // referencia dela. Em vez de descartar o lembrete em silencio, ele
              // vira horario proprio com o instante que estava valendo: o aviso
              // continua marcado, e agora visivel no campo.
              const relativo =
                modoLembrete !== SEM_LEMBRETE && modoLembrete !== LEMBRETE_PROPRIO;

              if (valor === '' && relativo) {
                setModoLembrete(LEMBRETE_PROPRIO);
                setLembreteProprio(
                  reminderAt === null ? '' : toLocalInput(reminderAt, timeZone),
                );
              }

              setDue(valor);
            }}
            className="min-h-touch rounded border border-border bg-surface px-3 text-base"
          />
        </div>

        {/*
          Lembrete (spec 4.4). O campo existia ponta a ponta desde a fase 2 — no
          schema, na API, no sync — e o cron de 5 min o consome desde a 7b; era a
          tela que nao oferecia onde marcar. Sem isto o Web Push so era
          alcancavel por UPDATE direto no D1.
        */}
        <div className="flex flex-col gap-1">
          <label htmlFor="detail-reminder" className="text-sm text-text-mut">
            Lembrete
          </label>
          <select
            id="detail-reminder"
            value={modoLembrete}
            onChange={(event) => setModoLembrete(event.target.value)}
            className="min-h-touch rounded border border-border bg-surface px-3 text-base"
          >
            <option value={SEM_LEMBRETE}>Sem lembrete</option>
            {/*
              As antecedencias so aparecem com prazo definido: "1 hora antes" de
              coisa nenhuma nao descreve horario nenhum. Sem prazo, resta marcar
              o instante — que continua sendo um pedido legitimo.
            */}
            {temPrazo
              ? LEMBRETE_PRESETS.map((preset) => (
                  <option key={preset.minutos} value={String(preset.minutos)}>
                    {preset.label}
                  </option>
                ))
              : null}
            <option value={LEMBRETE_PROPRIO}>Outro horário…</option>
          </select>

          {modoLembrete === LEMBRETE_PROPRIO ? (
            <input
              type="datetime-local"
              aria-label="Horário do lembrete"
              value={lembreteProprio}
              onChange={(event) => setLembreteProprio(event.target.value)}
              className="min-h-touch rounded border border-border bg-surface px-3 text-base"
            />
          ) : null}

          {/*
            As duas dicas abaixo cobrem os dois jeitos de marcar um lembrete e
            nao receber nada — que sem aviso viram "o app esta quebrado".
          */}
          {lembreteNoPassado ? (
            <p className="text-sm text-text-mut">
              Esse horário já passou. O aviso ainda sai no próximo ciclo se o atraso for
              menor que 6 horas; acima disso ele é descartado.
            </p>
          ) : null}

          {reminderAt !== null && !notificacoesAutorizadas() ? (
            <p className="text-sm text-text-mut">
              O aviso chega por notificação, e elas ainda não estão autorizadas neste
              aparelho. Ative em Mais → Ajustes.
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="detail-priority" className="text-sm text-text-mut">
            Prioridade
          </label>
          <select
            id="detail-priority"
            value={priority}
            onChange={(event) => setPriority(Number(event.target.value) as Priority)}
            className="min-h-touch rounded border border-border bg-surface px-3 text-base"
          >
            {PRIORIDADES.map((value) => (
              <option key={value} value={value}>
                {PRIORITY_LABEL_PT[value]}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="detail-estimate" className="text-sm text-text-mut">
            Pomodoros previstos
          </label>
          <input
            id="detail-estimate"
            type="number"
            min={0}
            max={99}
            inputMode="numeric"
            value={estimatePomos}
            onChange={(event) => setEstimatePomos(event.target.value)}
            placeholder="sem estimativa"
            className="min-h-touch rounded border border-border bg-surface px-3 text-base"
          />
        </div>

        {/*
          Uma subtarefa nao ganha subtarefas: a arvore existe no DDL
          (`parent_id` referencia a propria tabela) e a interface para em um
          nivel de proposito. Dois niveis ja pedem recolher, arrastar entre
          galhos e uma resposta para "concluir o pai conclui os filhos?" —
          decisoes que ninguem pediu, para um app de tres pessoas.
        */}
        {task.parentId === null ? <Subtarefas pai={task} /> : null}

        {/*
          Tags como checkboxes, e nao um `<select multiple>`: a spec 10.5 exige
          navegacao completa por teclado e alvo de 44px, e o multiplo nativo
          falha nos dois — no mobile ele vira uma lista minuscula, e no desktop
          exige Ctrl+clique para nao perder a selecao anterior.

          So aparece quando ha tag criada. Um bloco vazio com "nenhuma tag"
          ocuparia espaco permanente no painel para quem nunca vai usar tags.
        */}
        {(tagsData?.tags ?? []).length > 0 ? (
          <fieldset className="flex flex-col gap-1">
            <legend className="text-sm text-text-mut">Tags</legend>

            <div className="flex flex-wrap gap-1 pt-1">
              {(tagsData?.tags ?? []).map((tag) => {
                const marcada = tagIds.includes(tag.id);

                return (
                  <label
                    key={tag.id}
                    className={[
                      'flex min-h-touch cursor-pointer items-center gap-2 rounded-sm border px-2 text-sm transition-colors duration-150',
                      marcada
                        ? 'border-brand-blue bg-brand-blue-soft text-brand-blue'
                        : 'border-border hover:bg-surface-2',
                    ].join(' ')}
                  >
                    <input
                      type="checkbox"
                      checked={marcada}
                      onChange={() =>
                        setTagIds((atuais) =>
                          marcada
                            ? atuais.filter((id) => id !== tag.id)
                            : [...atuais, tag.id],
                        )
                      }
                      className="h-4 w-4 accent-brand-blue"
                    />
                    <span
                      aria-hidden="true"
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    {tag.name}
                  </label>
                );
              })}
            </div>
          </fieldset>
        ) : null}

        <div className="flex flex-col gap-1">
          <label htmlFor="detail-notes" className="text-sm text-text-mut">
            Notas
          </label>
          {/*
            Markdown em texto puro por enquanto. A renderizacao passa por
            marked + DOMPurify quando entrar (spec 6.9) — as notas sao o vetor
            de XSS mais provavel desta aplicacao, entao nada de
            `dangerouslySetInnerHTML` antes disso existir.
          */}
          <textarea
            id="detail-notes"
            value={notes}
            rows={5}
            onChange={(event) => setNotes(event.target.value)}
            className="rounded border border-border bg-surface p-3 text-base"
          />
        </div>

        {/*
          Rodape preso ao fundo da gaveta: Salvar e Excluir ficam visiveis com
          o formulario rolado em qualquer ponto. `sticky` funciona porque o
          ancestral que rola e o proprio <aside>. O padding de baixo respeita a
          area segura do iPhone (a barra de gesto).
        */}
        <div className="sticky bottom-0 -mx-4 flex items-center justify-between gap-2 border-t border-border bg-surface px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2">
          <button
            type="button"
            onClick={() => {
              deleteTask.mutate(task);
              onClose();
            }}
            className="min-h-touch rounded-sm px-3 text-sm text-overdue hover:bg-surface-2"
          >
            Excluir
          </button>

          <button
            type="submit"
            className="min-h-touch rounded bg-brand-blue px-4 font-medium text-white transition-colors duration-150 hover:bg-brand-blue-hover"
          >
            Salvar
          </button>
        </div>
      </form>
    </aside>
  );
}
